import { useState } from 'react';
import type { Media } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

export interface MediaCardProps {
  media: Media & { source?: string };
  isSelected?: boolean;
  onClick?: (media: Media) => void;
  onSelect?: (media: Media) => void;
  onToggleSelect?: (media: Media) => void;
  showCheckbox?: boolean;
  showPublicationStatus?: boolean;
  showTags?: boolean;
  showUploader?: boolean;
  showSource?: boolean;
  readOnly?: boolean;
  className?: string;
}

export function MediaCard({
  media,
  isSelected = false,
  onClick,
  onSelect,
  onToggleSelect,
  showCheckbox = false,
  showPublicationStatus = true,
  showTags = true,
  showUploader = false,
  showSource = false,
  readOnly = false,
  className = '',
}: MediaCardProps) {
  const [imageError, setImageError] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(media);
    } else if (onSelect) {
      onSelect(media);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(media);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`relative bg-gray-50 rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
        isSelected ? 'ring-2 ring-primary-500' : ''
      } ${className}`}
      onClick={handleClick}
    >
      {/* Checkbox for bulk selection */}
      {showCheckbox && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-primary-600 bg-gray-50 border-gray-300 rounded focus:ring-info-500"
          />
        </div>
      )}

      {/* Shared media indicator */}
      {media.entityType === 'shared' && (
        <div className="absolute top-2 left-2 z-10">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
            </svg>
            Shared
          </span>
        </div>
      )}

      {/* Source indicator */}
      {showSource && media.source && (
        <div className="absolute top-2 left-2 z-10">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            media.source === 'event' 
              ? 'bg-primary-100 text-primary-800 border border-primary-200'
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}>
            {media.source === 'event' ? '📅 Event' : '🏢 Venue'}
          </span>
        </div>
      )}

      {/* Read-only indicator */}
      {readOnly && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
            👁️ View Only
          </span>
        </div>
      )}

      {/* Publication status indicator */}
      {showPublicationStatus && media.entityType !== 'shared' && (
        <div className="absolute top-2 right-2 z-10">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              media.isPublished
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {media.isPublished ? 'Published' : 'Unpublished'}
          </span>
        </div>
      )}

      {/* Image thumbnail */}
      <div className="aspect-square bg-gray-100 relative">
        {!imageError ? (
          <ImgWithSkeleton
            src={media.cloudinaryUrl}
            alt={media.caption || media.filename}
            className="w-full h-full object-cover"
            skeletonClassName="rounded-t-lg"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Media info */}
      <div className="p-3">
        {/* Filename */}
        <h3 className="text-sm font-medium text-gray-900 truncate mb-1">
          {media.filename}
        </h3>

        {/* Caption if available */}
        {media.caption && (
          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
            {media.caption}
          </p>
        )}

        {/* Tags */}
        {showTags && media.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {media.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
              >
                {tag}
              </span>
            ))}
            {media.tags.length > 3 && (
              <span className="text-xs text-gray-500">
                +{media.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="text-xs text-gray-500 space-y-1">
          <div className="flex justify-between">
            <span>Size:</span>
            <span>{formatFileSize(media.sizeBytes)}</span>
          </div>
          {media.width && media.height && (
            <div className="flex justify-between">
              <span>Dimensions:</span>
              <span>{media.width} × {media.height}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Uploaded:</span>
            <span>{formatDate(media.createdAt)}</span>
          </div>
          {showUploader && (
            <div className="flex justify-between">
              <span>By:</span>
              <span className="truncate ml-1">User</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}